import React, { useCallback, useEffect, useState, useRef } from "react";
import { Issue, CustomEventType } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { FieldWrapper } from "@adsk/acc-issues-common/components/primitive/FieldWrapper";
import Theme from "@adsk/alloy-react-theme";
import styled, { keyframes } from "styled-components";
import { IssueSnapshotView } from "../IssueSnapshotView";
import { trackIssueSnapshotStatus } from "../../../utils/analytics/issueActionsTracking";
import {
  fetchIssueSnapshotAction,
  getIsCapturingIssueSnapshot,
  getIsIssueSnapshotWithMarkups,
  getIsPersistingIssueSnapshot,
  getIssueSnapshotUrl,
  getShouldFetchSnapshot,
  issueThumbnailFieldMounted,
  issueThumbnailFieldUnmounted,
} from "@adsk/acc-issues-core/state/redux/ducks/issueSnapshots";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";
import { AbsoluteCenteredSpinner } from "@adsk/acc-issues-common/components/primitive/CenteredSpinner";
import { useImageLoader } from "../../../hooks/useImageLoader";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import { IssueSnapshotImageErrorState } from "../IssueSnapshotImageErrorState";
import {
  CustomEventTarget,
  useFireCustomEvent,
  createCustomEvent,
  isDefaultPrevented,
} from "@adsk/acc-issues-core/providers/CustomEventContext";
import { useMonitoring } from "@adsk/acc-issues-core/utils/hooks/useMonitoring";

import { useIssuePlacementType } from "@adsk/acc-issues-core/utils/hooks/useIssuePlacementType";

type ThumbnailFieldProps = {
  issue: Issue;
  onThumbnailClick?: () => void;
};

const getFadeInKeyframes = (opacity = 1) => keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: ${opacity};
  }
`;

const ThumbnailContainer = styled.div`
  border: 1px solid ${Theme.colors.charcoal200};
  border-radius: 4px;
  position: relative;
  height: 160px;
  overflow: hidden;
  display: flex;
  justify-content: center;
`;

const ThumbnailImage = styled.img`
  flex: auto;
  object-fit: cover;
  cursor: pointer;
  opacity: ${(props: { uploading?: boolean }) => (props.uploading ? 0.4 : 1)};
  animation: ${(props: { uploading?: boolean }) =>
      getFadeInKeyframes(props.uploading ? 0.4 : 1)}
    0.1s ease-in;
  height: 100%;
  width: 100%;
`;

const ConditionalWrapper = styled.div`
  display: ${(props: { show: boolean }) => (props.show ? "block" : "none")};
`;

const ThumbnailUploadText = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 45px;
  text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff,
    1px 1px 0 #fff;
`;

export const ISSUE_THUMBNAIL_BUTTON_PLACEHOLDER = "issue-thumbnail-button";
export const ISSUE_THUMBNAIL_EMPTY_PLACEHOLDER = "issue-thumbnail-empty-state";

export const ThumbnailImageDisplay = ({
  issue,
  url,
  error,
  renderViewHeader,
  onThumbnailClick,
  renderSnapshotViewActionButtons,
}: {
  issue: Issue;
  url?: string;
  error?: boolean;
  renderViewHeader?: () => JSX.Element;
  onThumbnailClick?: () => void;
  renderSnapshotViewActionButtons?: () => JSX.Element;
}) => {
  const { T } = useTranslations();
  const isPersistingSnapshot = useSelector(getIsPersistingIssueSnapshot)(
    issue.id
  );
  const { isLoading: isLoadingImage } = useImageLoader(url);
  const fireCustomEvent = useFireCustomEvent();
  const [showSnapshotView, setShowSnapshotView] = useState(false);
  const issueSnapshotHasMarkups = useSelector(getIsIssueSnapshotWithMarkups)(
    issue.id
  );
  const imgRef = useRef<HTMLImageElement | null>(null);
  const getIssuePlacementType = useIssuePlacementType();

  const handleThumbnailClick = useCallback(() => {
    if (url) {
      const customEventData = createCustomEvent({
        type: CustomEventType.IssueSnapshotClick,
        target: CustomEventTarget.Image,
        data: { url: url },
      });
      if (fireCustomEvent) {
        fireCustomEvent(customEventData);
      }
      if (!fireCustomEvent || !isDefaultPrevented(customEventData)) {
        setShowSnapshotView(true);
      }
    }

    onThumbnailClick?.();
  }, [fireCustomEvent, url, onThumbnailClick]);

  const [showErrorState, setShowErrorState] = useState(error ?? false);
  const handleImageError = useCallback(e => {
    setShowErrorState(true);
    console.error(e);
  }, []);

  useEffect(() => {
    if (!error) {
      return () => {
        setShowErrorState(true);
      };
    }
  }, [error]);

  useEffect(() => {
    if (!isPersistingSnapshot) {
      return () => {
        setShowErrorState(false);
      };
    }
  }, [isPersistingSnapshot]);

  const placementType = getIssuePlacementType(issue);

  useEffect(() => {
    if (isPersistingSnapshot) {
      return () => {
        if (imgRef.current) {
          const snapshotImg = imgRef.current;
          trackIssueSnapshotStatus({
            issueId: issue.id,
            hasMarkups: issueSnapshotHasMarkups,
            hres: snapshotImg.naturalWidth,
            vres: snapshotImg.naturalHeight,
            placementType,
          });
        }
      };
    }
  }, [isPersistingSnapshot, issueSnapshotHasMarkups, issue.id]);

  if (showErrorState) {
    return <IssueSnapshotImageErrorState />;
  }

  if (!url || isLoadingImage) {
    return (
      <AbsoluteCenteredSpinner size={ProgressRing.SIZES.SMALL} delay={500} />
    );
  }

  return (
    <>
      <ThumbnailImage
        src={url}
        onClick={handleThumbnailClick}
        uploading={isPersistingSnapshot}
        onError={handleImageError}
        ref={imgRef}
        data-testid="thumbnail-image"
      />
      {isPersistingSnapshot && (
        <>
          <ThumbnailUploadText>
            {T.translate("thumbnailField.uploadingThumbnail")}
          </ThumbnailUploadText>
          <AbsoluteCenteredSpinner
            size={ProgressRing.SIZES.SMALL}
            delay={500}
          />
        </>
      )}
      <IssueSnapshotView
        imageUrl={url!}
        issue={issue}
        isOpen={showSnapshotView}
        onClose={() => setShowSnapshotView(false)}
        renderHeader={renderViewHeader}
        renderActionButtons={renderSnapshotViewActionButtons}
      />
    </>
  );
};

export const ThumbnailField = ({
  issue,
  onThumbnailClick,
}: ThumbnailFieldProps) => {
  const { T } = useTranslations();
  const issueSnapshotUrl = useSelector(getIssueSnapshotUrl)(issue.id);
  const isCapturingSnapshot = useSelector(getIsCapturingIssueSnapshot)(
    issue.id
  );
  const isPersistingSnapshot = useSelector(getIsPersistingIssueSnapshot)(
    issue.id
  );
  const shouldFetchSnapshot = useSelector(getShouldFetchSnapshot)(issue);
  const [isFailedToFetch, setIsFailedToFetch] = useState(false);
  const shouldRenderSnapshot =
    isPersistingSnapshot || !!issue.snapshotUrn || isCapturingSnapshot;
  const dispatch = useDispatch();
  const { logger } = useMonitoring();

  useEffect(() => {
    dispatch(issueThumbnailFieldMounted({ issueId: issue.id }));
    return () => {
      dispatch(issueThumbnailFieldUnmounted({ issueId: issue.id }));
    };
  }, [dispatch, issue.id]);

  useEffect(() => {
    if (!isFailedToFetch && shouldFetchSnapshot) {
      dispatch(
        fetchIssueSnapshotAction({
          issue,
          onError: (e: Error) => {
            logger.error(
              "Thumbnail Field",
              `Failed to fetch snapshot for issue ${issue.id}`,
              e
            );
            setIsFailedToFetch(true);
          },
        })
      );
    }
  }, [dispatch, isFailedToFetch, issue, logger, shouldFetchSnapshot]);

  // The empty divs with id are placeholders for buttons which are rendered using portal
  return (
    <>
      <ConditionalWrapper show={shouldRenderSnapshot}>
        <FieldWrapper
          label={T.translate("issueAttributes.thumbnail")}
          testId="thumbnail-field"
        >
          <ThumbnailContainer>
            <ThumbnailImageDisplay
              issue={issue}
              url={issueSnapshotUrl}
              error={isFailedToFetch}
              onThumbnailClick={onThumbnailClick}
            />
            <div id={ISSUE_THUMBNAIL_BUTTON_PLACEHOLDER}></div>
          </ThumbnailContainer>
        </FieldWrapper>
      </ConditionalWrapper>
      <ConditionalWrapper show={!shouldRenderSnapshot}>
        <div id={ISSUE_THUMBNAIL_EMPTY_PLACEHOLDER}></div>
      </ConditionalWrapper>
    </>
  );
};
